/**
 * Whether a Meta picker's child level (ad sets under a campaign, ads under an
 * ad set) should widen its fetch to the full inventory (every status) right
 * after the parent was chosen — PRD docs/prd/attribution-korrektur.md Story
 * S3, Epic A5; fixed in TASK-74.
 *
 * A non-ACTIVE parent (paused, archived, completed...) practically never has
 * ACTIVE children, so leaving the child fetch on "active only" produces an
 * empty list for exactly the case this picker exists for: attributing a
 * touchpoint after the fact, which is almost always an older, no-longer-
 * active campaign. The parent's status ships with the picker item already
 * (`MetaTreeItem.status`) — no extra fetch needed.
 *
 * `undefined` (parent cleared, or the picked id wasn't found) behaves like an
 * ACTIVE parent — don't widen; matches the pre-TASK-74 default, and the child
 * fetch stays disabled anyway once there is no parent id.
 */
export function shouldWidenChildFetch(parentStatus: string | undefined): boolean {
  return parentStatus !== undefined && parentStatus !== "ACTIVE";
}

/**
 * Combines the two independent reasons a Meta picker's child level might
 * need every status, not just active: the parent's status
 * (`shouldWidenChildFetch`, evaluated once when the parent is picked) and
 * whatever the operator has typed into the child level's own search box.
 * Callers must keep these as two separate pieces of state and OR them here
 * rather than writing both into one flag — otherwise deleting the search
 * text re-narrows the fetch to "active only" and empties the list again for
 * a still-selected, still-non-active parent (TASK-74 review follow-up F1).
 */
export function shouldFetchAllChildren(widenedByParentStatus: boolean, searching: boolean): boolean {
  return widenedByParentStatus || searching;
}
